// Teacher page (SPEC §7.5): details, login, week grid, conflict banner, unavailability, edit, archive.

import type { ApiResult } from "../../api/client";
import { fetchCourses } from "../../api/courses";
import {
  addTeacherPeriod, fetchTeacher, fetchTeacherWeek, removeTeacherPeriod, setTeacherArchived, updateTeacher,
  type NewPeriod, type TeacherFields,
} from "../../api/teachers";
import type { Course, DayGrid, Lesson, Teacher, TeacherWeek } from "../../api/types";
import { mondayOf } from "../../dates";
import { formatDate, t } from "../../i18n/i18n";
import { navigate, renderCurrentRoute, setRedraw, type RouteMatch } from "../../router";
import { isAdmin, requireSchool } from "../../session";
import { buildDateNavigator } from "../../ui/dateNavigator";
import { createBadge, createButton, createElement, createLink } from "../../ui/dom";
import { describeProblem, showToast } from "../../ui/feedback";
import { gridToColumn } from "../../ui/gridColumns";
import { lessonRoute } from "../../ui/lessonLabels";
import { buildAffectedLessons, buildPeriodsSection } from "../../ui/periodsSection";
import { buildTimeline } from "../../ui/timeline";
import { buildContacts } from "../lesson/candidatesTable";
import { createLoading, showPageProblem, startPage } from "../pageFrame";
import { buildTeacherForm } from "./teacherForm";

interface TeacherPageState {
  teacher: Teacher;
  week: TeacherWeek;
  courses: Course[];
  isEditing: boolean;
  affected: Lesson[] | null;
}

export async function renderTeacherPage(container: HTMLElement, route: RouteMatch): Promise<void> {
  const page: HTMLElement = startPage(container, t("teachers.title"));
  page.append(createLoading());
  const id: string = route.params.id ?? "";
  const from: string = mondayOf(route.query.get("from") ?? requireSchool().today);
  const teacher: ApiResult<Teacher> = await fetchTeacher(id);
  const week: ApiResult<TeacherWeek> = await fetchTeacherWeek(id, from);
  const courses: ApiResult<Course[]> = await fetchCourses(true);
  if (teacher.ok === false || week.ok === false || courses.ok === false) {
    showPageProblem(page, firstProblem([teacher, week, courses]));
    return;
  }

  const state: TeacherPageState = {
    teacher: teacher.data, week: week.data, courses: courses.data, isEditing: false, affected: null,
  };
  const draw = (): void => {
    drawPage(container, state);
  };
  setRedraw(container, draw);
  draw();
}

function drawPage(container: HTMLElement, state: TeacherPageState): void {
  const teacher: Teacher = state.teacher;
  const page: HTMLElement = startPage(container, `${teacher.firstName} ${teacher.lastName}`);
  page.append(buildDetails(state));
  if (isAdmin()) {
    page.append(buildAdminActions(container, state));
  }

  if (state.isEditing) {
    page.append(buildEditForm(state));
  }

  if (state.affected !== null) {
    page.append(buildAffectedLessons(state.affected));
  }

  page.append(...buildWeekSection(state.week), buildPeriodsSection({
    periods: teacher.unavailability,
    reasons: ["Sick", "Vacation", "Holiday", "Other"],
    canEdit: isAdmin(),
    add: (period: NewPeriod) => addTeacherPeriod(teacher.id, period),
    remove: (periodId: string) => removeTeacherPeriod(teacher.id, periodId),
    onChanged: (affected: Lesson[] | null): void => {
      void reloadTeacher(container, state, affected);
    },
  }));
}

function buildDetails(state: TeacherPageState): HTMLElement {
  const teacher: Teacher = state.teacher;
  const card: HTMLDivElement = createElement("div", "card");
  const courses: string = teacher.courseIds
    .map((id: string): string => state.courses.find((course: Course): boolean => course.id === id)?.name ?? id)
    .join(", ");
  card.append(createElement("p", "", `${t("teachers.courses")}: ${courses || t("app.none")}`),
    buildContacts(teacher));
  if (teacher.note !== null) {
    card.append(createElement("p", "muted", teacher.note));
  }

  if (teacher.archived) {
    card.append(createBadge(t("app.archived"), "neutral"));
  }

  card.append(buildLoginInfo(teacher));
  return card;
}

/** "Create login" or "Login: name, active/inactive" — admins only (SPEC §7.8 shortcut). */
function buildLoginInfo(teacher: Teacher): HTMLElement {
  const line: HTMLParagraphElement = createElement("p");
  if (isAdmin() === false) {
    return line;
  }

  if (teacher.login === null) {
    line.append(createLink(`/admin/users?newTeacher=${teacher.id}`, t("teachers.createLogin")));
    return line;
  }

  const status: string = teacher.login.active ? t("teachers.active") : t("teachers.inactive");
  line.append(t("teachers.loginStatus", { username: teacher.login.username, status }));
  return line;
}

function buildAdminActions(container: HTMLElement, state: TeacherPageState): HTMLElement {
  const actions: HTMLDivElement = createElement("div", "lesson-actions");
  const teacher: Teacher = state.teacher;
  actions.append(createButton(t("app.edit"), (): void => {
    state.isEditing = state.isEditing === false;
    drawPage(container, state);
  }), createButton(teacher.archived ? t("app.unarchive") : t("app.archive"), async (): Promise<void> => {
    const result: ApiResult<Teacher> = await setTeacherArchived(teacher.id, teacher.archived === false,
      teacher.version);
    showToast(result.ok ? t("app.saved") : describeProblem(result.problem));
    await renderCurrentRoute();
  }));
  return actions;
}

function buildEditForm(state: TeacherPageState): HTMLElement {
  return buildTeacherForm({
    teacher: state.teacher,
    courses: state.courses,
    save: (fields: TeacherFields) => updateTeacher(state.teacher.id, fields, state.teacher.version),
    onSaved: (): void => {
      showToast(t("app.saved"));
      void renderCurrentRoute();
    },
  });
}

/** Week grid, Monday to Sunday, and the banner of lessons during unavailable time (SPEC §7.5). */
function buildWeekSection(week: TeacherWeek): HTMLElement[] {
  const navigator: HTMLElement = buildDateNavigator(week.from, 7, (date: string): void => {
    navigate(`/teachers/${week.teacherId}?from=${mondayOf(date)}`);
  });
  const columns = week.days.map((day: DayGrid) => gridToColumn(formatDate(day.date), null, day));
  return [createElement("h2", "", t("teachers.week")), navigator, buildConflictBanner(week), buildTimeline(columns)];
}

function buildConflictBanner(week: TeacherWeek): HTMLElement {
  const lessons: Lesson[] = week.days.flatMap((day: DayGrid): Lesson[] => day.lessons.filter(
    (lesson: Lesson): boolean => day.problems.some((problem) => problem.kind === "LessonDuringUnavailableTime"
      && problem.lesson.slotId === lesson.key.slotId && problem.lesson.groupId === lesson.key.groupId)));
  const banner: HTMLDivElement = createElement("div", "banner");
  banner.hidden = lessons.length === 0;
  banner.append(createElement("p", "", t("teachers.conflictBanner")));
  for (const lesson of lessons) {
    banner.append(createLink(lessonRoute(lesson), `${formatDate(lesson.key.date)} ${lesson.groupName}`), " ");
  }

  return banner;
}

async function reloadTeacher(container: HTMLElement, state: TeacherPageState, affected: Lesson[] | null):
  Promise<void> {
  const teacher: ApiResult<Teacher> = await fetchTeacher(state.teacher.id);
  const week: ApiResult<TeacherWeek> = await fetchTeacherWeek(state.teacher.id, state.week.from);
  if (teacher.ok && week.ok) {
    state.teacher = teacher.data;
    state.week = week.data;
  }

  state.affected = affected;
  drawPage(container, state);
}

function firstProblem(results: ApiResult<unknown>[]): Parameters<typeof showPageProblem>[1] {
  for (const result of results) {
    if (result.ok === false) {
      return result.problem;
    }
  }

  throw new Error("firstProblem called without a failed result");
}
